/**
 * E-Invoice Mixin
 *
 * Shared logic for e-invoice components:
 * - Status display helpers
 * - Permission checks
 * - Common formatters
 * - State machine guards
 */
import { mapGetters } from 'vuex'
import moment from 'moment'
import {
  EINVOICE_STATUS,
  EINVOICE_STATUS_NAME,
  EINVOICE_STATUS_VARIANT,
  EINVOICE_STATUS_ICON,
  EINVOICE_SALE_TYPE,
  EINVOICE_TRANSITIONS,
  EINVOICE_REASON_CODE_NAME,
} from '@/utils/einvoice.enum'
import {
  buildLifecycleUiMeta,
  buildReadinessMeta,
  resolveLifecycleActionAllowance,
} from '@/view/pages/einvoice/utils/lifecycleState.utils'
import permissionMixin from '@/view/mixins/permission.mixin'

export default {
  mixins: [permissionMixin],
  computed: {
    ...mapGetters([
      'einvoiceList',
      'einvoiceTotalItems',
      'einvoiceTotalPages',
      'einvoiceDetail',
      'einvoiceFilter',
      'einvoiceIsLoading',
      'einvoiceIsDetailLoading',
      'einvoiceIsActionLoading',
      'einvoiceCanIssue',
      'einvoiceCanCorrect',
      'einvoiceCanRefreshTax',
      'einvoiceCanEdit',
      'einvoiceCanDownloadPdf',
      'einvoiceIsRetailOnly',
      'einvoiceIsRetail',
      'einvoiceBulkIssue',
    ]),
  },
  methods: {
    hasAllowedAction(invoice, action) {
      return resolveLifecycleActionAllowance(invoice, action)
    },
    getReadinessBlockingMessages(invoice = {}, readinessMeta = null) {
      const messages = Array.isArray(invoice?.readiness?.blockingErrors)
        ? invoice.readiness.blockingErrors
            .map((item) => item?.message || item)
            .map((item) => String(item || '').trim())
            .filter(Boolean)
        : []

      if (!messages.length && readinessMeta?.blockingErrorCount > 0) {
        messages.push(
          `Có ${readinessMeta.blockingErrorCount} lỗi blocker cần xử lý trước khi phát hành`,
        )
      }

      return messages
    },
    getInvoiceActionGuardContext(invoice) {
      if (!invoice) {
        return {
          readiness: buildReadinessMeta({}),
          canEdit: false,
          canIssue: false,
          canDownloadPdf: false,
          canValidateDraft: false,
          canPreviewDraft: false,
          canRefreshTax: false,
          canCancel: false,
          canAdjust: false,
          canReplace: false,
          canResendEmail: false,
          canDeleteDraft: false,
          issueBlockedByReadiness: false,
          issueBlockingMessages: [],
          issueDisabledReason: '',
        }
      }

      const readiness = buildReadinessMeta(invoice)
      const saleType = invoice.saleType

      const hasIssuePermission =
        saleType === EINVOICE_SALE_TYPE.RETAIL ? this.canIssueRetail() : this.canIssueWholesale()
      const hasEditPermission = this.canEditInvoice(saleType)
      const hasCancelPermission = this.canCancelInvoice(saleType)
      const hasAdjustPermission = this.canAdjustInvoice(saleType)
      const hasReplacePermission = this.canReplaceInvoice(saleType)

      const isServerActionAllowed = (action) => this.hasAllowedAction(invoice, action) === true
      const canEdit = isServerActionAllowed('update') && hasEditPermission
      const canIssue = isServerActionAllowed('issue') && hasIssuePermission
      const canDownloadPdf = isServerActionAllowed('download-pdf')
      const canValidateDraft = isServerActionAllowed('validate')
      const canPreviewDraft = isServerActionAllowed('preview')
      const canRefreshTax =
        isServerActionAllowed('query-tax-status') || isServerActionAllowed('query-status')
      const canCancel = isServerActionAllowed('cancel') && hasCancelPermission
      const canAdjust = isServerActionAllowed('adjust') && hasAdjustPermission
      const canReplace = isServerActionAllowed('replace') && hasReplacePermission
      const canResendEmail =
        isServerActionAllowed('resend-email') && saleType === EINVOICE_SALE_TYPE.WHOLESALE
      const canDeleteDraft = isServerActionAllowed('delete')

      const issueBlockedByReadiness = readiness.hasBlockingErrors
      const issueBlockingMessages = this.getReadinessBlockingMessages(invoice, readiness)
      const blockingReasonMessage = Array.isArray(invoice?.actionContext?.blockingReasons)
        ? invoice.actionContext.blockingReasons
            .map((item) => item?.message || item)
            .map((item) => String(item || '').trim())
            .filter(Boolean)
            .join('; ')
        : ''
      const issueDisabledReason = issueBlockedByReadiness
        ? issueBlockingMessages.join('; ')
        : blockingReasonMessage

      return {
        readiness,
        canEdit,
        canIssue,
        canDownloadPdf,
        canValidateDraft,
        canPreviewDraft,
        canRefreshTax,
        canCancel,
        canAdjust,
        canReplace,
        canResendEmail,
        canDeleteDraft,
        issueBlockedByReadiness,
        issueBlockingMessages,
        issueDisabledReason,
      }
    },
    getLifecycleUiMeta(invoice) {
      return buildLifecycleUiMeta(invoice)
    },

    // ── Status helpers ─────────────────────────────────────
    getStatusName(status) {
      return EINVOICE_STATUS_NAME[status] || 'Không xác định'
    },
    getStatusVariant(status) {
      return EINVOICE_STATUS_VARIANT[status] || 'secondary'
    },
    getStatusIcon(status) {
      return EINVOICE_STATUS_ICON[status] || 'fas fa-question-circle'
    },
    isTerminalStatus(status) {
      return [
        EINVOICE_STATUS.CANCELED,
        EINVOICE_STATUS.ADJUSTED,
        EINVOICE_STATUS.REPLACED,
      ].includes(status)
    },
    canTransitionTo(currentStatus, targetStatus) {
      const allowed = EINVOICE_TRANSITIONS[currentStatus] || []
      return allowed.includes(targetStatus)
    },

    // ── Permission helpers ─────────────────────────────────
    canViewRetail() {
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canIssueRetail() {
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canViewWholesale() {
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canIssueWholesale() {
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canCancelInvoice(saleType) {
      void saleType
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canAdjustInvoice(saleType) {
      void saleType
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canReplaceInvoice(saleType) {
      void saleType
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    canEditInvoice(saleType) {
      void saleType
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },

    // ── Formatters ─────────────────────────────────────────
    formatInvoiceDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY HH:mm')
    },
    formatInvoiceDateShort(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '0'
      return new Intl.NumberFormat('vi-VN').format(amount)
    },
    getReasonCodeName(reasonCode) {
      return EINVOICE_REASON_CODE_NAME[reasonCode] || reasonCode || ''
    },

    // ── Sale type helpers ──────────────────────────────────
    getSaleTypeName(saleType) {
      if (saleType === EINVOICE_SALE_TYPE.RETAIL) return 'Bán lẻ'
      if (saleType === EINVOICE_SALE_TYPE.WHOLESALE) return 'Bán buôn'
      return 'Không xác định'
    },
    getSaleTypeIcon(saleType) {
      if (saleType === EINVOICE_SALE_TYPE.RETAIL) return 'fas fa-cash-register'
      if (saleType === EINVOICE_SALE_TYPE.WHOLESALE) return 'fas fa-building'
      return 'fas fa-question-circle'
    },
    canEditInvoiceByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canEdit
    },
    canIssueInvoiceByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canIssue
    },
    canDownloadPdfByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canDownloadPdf
    },
    canValidateDraftByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canValidateDraft
    },
    canPreviewDraftByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canPreviewDraft
    },
    canRefreshTaxByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canRefreshTax
    },
    canCancelByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canCancel
    },
    canAdjustByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canAdjust
    },
    canReplaceByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canReplace
    },
    canResendEmailByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canResendEmail
    },
    canDeleteDraftByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).canDeleteDraft
    },
    getIssueDisabledReasonByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).issueDisabledReason
    },
    hasIssueBlockingReadinessByContext(invoice) {
      return this.getInvoiceActionGuardContext(invoice).issueBlockedByReadiness
    },
  },
}
